import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import RefreshIcon from '@material-ui/icons/Refresh';
import CategoryIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import AppsIcon from '@material-ui/icons/Apps';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import LiveHelpIcon from '@material-ui/icons/LiveHelp';
import TimelineIcon from '@material-ui/icons/Timeline';
import ChatIcon from '@material-ui/icons/Chat';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

const TYPE_COLORS = [
  '#8b5cf6',
  '#38bdf8',
  '#2dd4bf',
  '#f472b6',
  '#fbbf24',
  '#94a3b8',
];

type PortalStats = {
  components: { total: number; byType: Record<string, number> };
  apis: { total: number; byType: Record<string, number> };
  systems: number;
  docs: { withDocs: number; total: number; pct: number };
  apps: { count: number; names: string[] };
  videos: number;
  qeta: number;
  policies: {
    total: number;
    actionable: Array<{
      id?: string;
      name?: string;
      technology?: string;
      status?: string;
      version?: string;
    }>;
  };
  ai: { chunks: number; bySource: Record<string, number> };
};

async function safeJson<T>(
  fetchApi: { fetch: typeof fetch },
  url: string,
): Promise<T | null> {
  try {
    const res = await fetchApi.fetch(url);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

function countBy(
  items: Array<{ spec?: { type?: string } }>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const item of items) {
    const key = (item.spec?.type || 'other').toLowerCase();
    out[key] = (out[key] || 0) + 1;
  }
  return out;
}

function formatLabel(key: string) {
  if (!key) return 'Other';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap' as const,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  title: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: 'var(--content-primary, ' + theme.palette.text.primary + ')',
  },
  engineBadge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    padding: '3px 8px',
    borderRadius: 6,
    color: 'var(--interactive-primary, ' + theme.palette.primary.main + ')',
    backgroundColor:
      'var(--color-primary-transparent-16, rgba(110,58,143,0.16))',
    border: '1px solid var(--color-primary-transparent-32, rgba(110,58,143,0.32))',
  },
  hint: {
    fontSize: '0.8125rem',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
    marginTop: 2,
  },
  refreshMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '0.75rem',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
  },
  refreshBtn: {
    textTransform: 'none' as const,
    fontSize: '0.75rem',
    minWidth: 0,
    padding: theme.spacing(0.25, 1),
  },
  card: {
    height: '100%',
    padding: theme.spacing(2),
    borderRadius: 12,
    border: '1px solid var(--color-divider, ' + theme.palette.divider + ')',
    backgroundColor:
      'var(--surface-paper, ' + theme.palette.background.paper + ')',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1.25),
    textDecoration: 'none',
    color: 'inherit',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      borderColor:
        'var(--color-primary-transparent-32, rgba(110,58,143,0.32))',
      boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    },
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      'var(--color-primary-transparent-16, rgba(110,58,143,0.16))',
    color: 'var(--interactive-primary, ' + theme.palette.primary.main + ')',
  },
  pill: {
    fontSize: '0.65rem',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 999,
    whiteSpace: 'nowrap' as const,
  },
  value: {
    fontWeight: 800,
    fontSize: '2rem',
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: 'var(--content-primary, ' + theme.palette.text.primary + ')',
  },
  valueSm: {
    fontWeight: 800,
    fontSize: '1.6rem',
    lineHeight: 1.1,
    color: 'var(--content-primary, ' + theme.palette.text.primary + ')',
  },
  caption: {
    fontSize: '0.75rem',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
  },
  stackedBar: {
    display: 'flex',
    height: 10,
    borderRadius: 999,
    overflow: 'hidden',
    backgroundColor: 'rgba(145,158,171,0.12)',
    marginTop: 4,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1, 1.5),
    marginTop: 6,
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.7rem',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
  },
  swatch: {
    width: 8,
    height: 8,
    borderRadius: 2,
  },
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginTop: 4,
  },
  typeCell: {
    borderRadius: 8,
    padding: theme.spacing(1),
    backgroundColor: 'rgba(145,158,171,0.08)',
    border: '1px solid var(--color-divider, ' + theme.palette.divider + ')',
    textAlign: 'center' as const,
  },
  typeCellValue: {
    fontWeight: 700,
    fontSize: '1rem',
    color: 'var(--content-primary, ' + theme.palette.text.primary + ')',
  },
  typeCellLabel: {
    fontSize: '0.65rem',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
    marginTop: 2,
  },
  docsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  ringWrap: {
    position: 'relative' as const,
    width: 72,
    height: 72,
    flexShrink: 0,
  },
  ringLabel: {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: '0.85rem',
  },
  okLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.75rem',
    color: '#22c55e',
    marginTop: 'auto',
  },
  spark: {
    width: '100%',
    height: 44,
    marginTop: 4,
  },
  sourceChips: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 6,
    marginTop: 4,
  },
  chip: {
    fontSize: '0.65rem',
    padding: '2px 6px',
    borderRadius: 6,
    backgroundColor: 'rgba(145,158,171,0.1)',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
  },
  miniCard: {
    height: '100%',
    padding: theme.spacing(1.75),
    borderRadius: 12,
    border: '1px solid var(--color-divider, ' + theme.palette.divider + ')',
    backgroundColor:
      'var(--surface-paper, ' + theme.palette.background.paper + ')',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    transition: 'border-color 0.15s ease',
    '&:hover': {
      borderColor:
        'var(--color-primary-transparent-32, rgba(110,58,143,0.32))',
    },
  },
  panel: {
    height: '100%',
    padding: theme.spacing(2),
    borderRadius: 12,
    border: '1px solid var(--color-divider, ' + theme.palette.divider + ')',
    backgroundColor:
      'var(--surface-paper, ' + theme.palette.background.paper + ')',
  },
  panelTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    fontSize: '0.95rem',
    marginBottom: theme.spacing(0.5),
  },
  alertItem: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.25),
    borderRadius: 10,
    backgroundColor: 'rgba(251, 191, 36, 0.08)',
    border: '1px solid rgba(251, 191, 36, 0.28)',
  },
  alertName: {
    fontWeight: 700,
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  severity: {
    fontSize: '0.65rem',
    fontWeight: 800,
    padding: '2px 6px',
    borderRadius: 6,
    backgroundColor: 'rgba(239, 68, 68, 0.18)',
    color: '#f87171',
  },
  healthRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: 4,
  },
  healthBar: {
    height: 8,
    borderRadius: 999,
    marginBottom: theme.spacing(1.5),
  },
  activity: {
    marginTop: theme.spacing(1),
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: '0.78rem',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
    marginBottom: 8,
  },
  activityDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    marginTop: 5,
    flexShrink: 0,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6),
  },
  linkReset: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    height: '100%',
  },
}));

function StackedBar({
  parts,
}: {
  parts: Array<{ key: string; value: number; color: string }>;
}) {
  const classes = useStyles();
  const total = parts.reduce((a, b) => a + b.value, 0) || 1;
  return (
    <>
      <div className={classes.stackedBar}>
        {parts.map(p => (
          <div
            key={p.key}
            style={{
              width: `${(p.value / total) * 100}%`,
              backgroundColor: p.color,
              minWidth: p.value > 0 ? 4 : 0,
            }}
            title={`${formatLabel(p.key)}: ${p.value}`}
          />
        ))}
      </div>
      <div className={classes.legend}>
        {parts.slice(0, 4).map(p => (
          <span key={p.key} className={classes.legendItem}>
            <span
              className={classes.swatch}
              style={{ backgroundColor: p.color }}
            />
            {formatLabel(p.key)} · {p.value}
          </span>
        ))}
      </div>
    </>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const classes = useStyles();
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 200;
  const h = 44;
  const pts = values
    .map((v, i) => {
      const x = (i / Math.max(values.length - 1, 1)) * w;
      const y = h - ((v - min) / range) * (h - 6) - 3;
      return `${x},${y}`;
    })
    .join(' ');
  const area = `0,${h} ${pts} ${w},${h}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={classes.spark} preserveAspectRatio="none">
      <defs>
        <linearGradient id="aiSparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#aiSparkFill)" />
      <polyline
        points={pts}
        fill="none"
        stroke="#a78bfa"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Pill({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warn' | 'accent';
}) {
  const classes = useStyles();
  const styles: Record<string, CSSProperties> = {
    neutral: {
      backgroundColor: 'rgba(145,158,171,0.12)',
      color: 'var(--content-secondary, #919eab)',
    },
    success: { backgroundColor: 'rgba(34,197,94,0.15)', color: '#4ade80' },
    warn: { backgroundColor: 'rgba(251,191,36,0.15)', color: '#fbbf24' },
    accent: {
      backgroundColor: 'rgba(139,92,246,0.16)',
      color: '#c4b5fd',
    },
  };
  return (
    <span className={classes.pill} style={styles[tone]}>
      {children}
    </span>
  );
}

export function SystemMetrics() {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<PortalStats | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        components,
        apis,
        systems,
        withDocs,
        appsBase,
        videosBase,
        qetaBase,
        policiesBase,
        aiBase,
      ] = await Promise.all([
        catalogApi.getEntities({
          filter: { kind: 'Component' },
          fields: ['metadata.name', 'spec.type'],
        }),
        catalogApi.getEntities({
          filter: { kind: 'API' },
          fields: ['metadata.name', 'spec.type'],
        }),
        catalogApi.getEntities({
          filter: { kind: 'System' },
          fields: ['metadata.name'],
        }),
        catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'metadata.annotations.backstage.io/techdocs-ref':
              CATALOG_FILTER_EXISTS,
          },
          fields: ['metadata.name'],
        }),
        discoveryApi.getBaseUrl('applications'),
        discoveryApi.getBaseUrl('videos'),
        discoveryApi.getBaseUrl('qeta'),
        discoveryApi.getBaseUrl('stack-policies'),
        discoveryApi.getBaseUrl('ai-assistant'),
      ]);

      const [apps, videos, qeta, policies, aiStatus] = await Promise.all([
        safeJson<Array<{ name?: string; title?: string }>>(
          fetchApi,
          `${appsBase}/applications`,
        ),
        safeJson<unknown[]>(fetchApi, `${videosBase}/videos`),
        safeJson<{ total?: number; posts?: unknown[] }>(
          fetchApi,
          `${qetaBase}/posts?type=question&limit=1`,
        ),
        safeJson<
          Array<{
            id?: string;
            name?: string;
            technology?: string;
            status?: string;
            version?: string;
          }>
        >(fetchApi, `${policiesBase}/policies`),
        safeJson<{ chunkCounts?: Record<string, number> }>(
          fetchApi,
          `${aiBase}/index/status`,
        ),
      ]);

      const componentTotal = components.items.length;
      const docsCount = withDocs.items.length;
      const qetaTotal =
        typeof qeta?.total === 'number'
          ? qeta.total
          : Array.isArray(qeta?.posts)
            ? qeta.posts.length
            : 0;

      const actionable = Array.isArray(policies)
        ? policies.filter(p =>
            ['migrate_on_touch', 'deprecated', 'banned'].includes(
              p.status || '',
            ),
          )
        : [];

      const bySource = aiStatus?.chunkCounts || {};
      const chunks = Object.values(bySource).reduce((a, b) => a + b, 0);

      setStats({
        components: {
          total: componentTotal,
          byType: countBy(components.items as any),
        },
        apis: {
          total: apis.items.length,
          byType: countBy(apis.items as any),
        },
        systems: systems.items.length,
        docs: {
          withDocs: docsCount,
          total: componentTotal,
          pct:
            componentTotal > 0
              ? Math.round((docsCount / componentTotal) * 100)
              : 0,
        },
        apps: {
          count: Array.isArray(apps) ? apps.length : 0,
          names: Array.isArray(apps)
            ? apps
                .slice(0, 2)
                .map(a => a.title || a.name || 'App')
                .filter(Boolean)
            : [],
        },
        videos: Array.isArray(videos) ? videos.length : 0,
        qeta: qetaTotal,
        policies: {
          total: Array.isArray(policies) ? policies.length : 0,
          actionable,
        },
        ai: { chunks, bySource },
      });
      setUpdatedAt(new Date());
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [catalogApi, discoveryApi, fetchApi]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 60_000);
    return () => window.clearInterval(id);
  }, [load]);

  const componentParts = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.components.byType)
      .sort((a, b) => b[1] - a[1])
      .map(([key, value], i) => ({
        key,
        value,
        color: TYPE_COLORS[i % TYPE_COLORS.length],
      }));
  }, [stats]);

  const apiEntries = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.apis.byType)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  }, [stats]);

  const sparkValues = useMemo(() => {
    if (!stats) return [2, 4, 3, 6, 5, 8, 7, 9];
    const vals = Object.values(stats.ai.bySource);
    if (vals.length >= 3) return vals;
    const base = stats.ai.chunks || 10;
    return [0.4, 0.55, 0.5, 0.7, 0.65, 0.85, 0.8, 1].map(r =>
      Math.round(base * r),
    );
  }, [stats]);

  if (loading && !stats) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!stats) {
    return (
      <Typography variant="body2" color="textSecondary">
        No se pudieron cargar las métricas del sistema.
      </Typography>
    );
  }

  const compliance = Math.min(
    100,
    Math.round(
      stats.docs.pct * 0.6 +
        (stats.policies.total > 0
          ? ((stats.policies.total - stats.policies.actionable.length) /
              stats.policies.total) *
            40
          : 40),
    ),
  );

  return (
    <Box className={classes.root}>
      <div className={classes.sectionHeader}>
        <div>
          <div className={classes.titleRow}>
            <Typography className={classes.title}>Panorama del portal</Typography>
            <span className={classes.engineBadge}>Fluxem Engine</span>
          </div>
          <Typography className={classes.hint}>
            Datos en vivo del catálogo, documentación y plugins de Fluxem
          </Typography>
        </div>
        <div className={classes.refreshMeta}>
          <span>
            Sondeo cada 60s
            {updatedAt
              ? ` · ${updatedAt.toLocaleTimeString('es-CO', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`
              : ''}
          </span>
          <Button
            className={classes.refreshBtn}
            size="small"
            startIcon={<RefreshIcon style={{ fontSize: 14 }} />}
            onClick={() => load()}
            disabled={loading}
          >
            Forzar refresh
          </Button>
        </div>
      </div>

      <Grid container spacing={2}>
        {/* Primary cards */}
        <Grid item xs={12} sm={6} lg={3}>
          <RouterLink to="/catalog" className={classes.linkReset}>
            <Paper className={classes.card} elevation={0}>
              <div className={classes.cardTop}>
                <span className={classes.cardTitle}>
                  <span className={classes.iconWrap}>
                    <CategoryIcon fontSize="small" />
                  </span>
                  Componentes
                </span>
                <Pill tone="accent">{stats.components.total} en catálogo</Pill>
              </div>
              <Typography className={classes.value}>
                {stats.components.total}
              </Typography>
              <Typography className={classes.caption}>
                Distribución por tipo
              </Typography>
              <StackedBar parts={componentParts} />
            </Paper>
          </RouterLink>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <RouterLink to="/api-docs" className={classes.linkReset}>
            <Paper className={classes.card} elevation={0}>
              <div className={classes.cardTop}>
                <span className={classes.cardTitle}>
                  <span className={classes.iconWrap}>
                    <ExtensionIcon fontSize="small" />
                  </span>
                  APIs
                </span>
                <Pill>Contratos</Pill>
              </div>
              <Typography className={classes.value}>{stats.apis.total}</Typography>
              <div className={classes.typeGrid}>
                {(apiEntries.length
                  ? apiEntries
                  : ([['openapi', 0], ['graphql', 0], ['grpc', 0]] as Array<
                      [string, number]
                    >)
                ).map(([key, value]) => (
                  <div key={key} className={classes.typeCell}>
                    <div className={classes.typeCellValue}>{value}</div>
                    <div className={classes.typeCellLabel}>
                      {formatLabel(key)}
                    </div>
                  </div>
                ))}
              </div>
            </Paper>
          </RouterLink>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <RouterLink to="/docs" className={classes.linkReset}>
            <Paper className={classes.card} elevation={0}>
              <div className={classes.cardTop}>
                <span className={classes.cardTitle}>
                  <span className={classes.iconWrap}>
                    <LibraryBooksIcon fontSize="small" />
                  </span>
                  Con TechDocs
                </span>
                <Pill tone={stats.docs.pct >= 80 ? 'success' : 'warn'}>
                  {stats.docs.pct}% cobertura
                </Pill>
              </div>
              <div className={classes.docsRow}>
                <Typography className={classes.value}>
                  {stats.docs.withDocs}
                  <span
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      opacity: 0.55,
                    }}
                  >
                    {' '}
                    / {stats.docs.total}
                  </span>
                </Typography>
                <div className={classes.ringWrap}>
                  <CircularProgress
                    variant="static"
                    value={100}
                    size={72}
                    thickness={3}
                    style={{
                      color: 'rgba(145,158,171,0.15)',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                    }}
                  />
                  <CircularProgress
                    variant="static"
                    value={stats.docs.pct}
                    size={72}
                    thickness={3}
                    style={{
                      color: stats.docs.pct >= 80 ? '#22c55e' : '#fbbf24',
                    }}
                  />
                  <span className={classes.ringLabel}>{stats.docs.pct}%</span>
                </div>
              </div>
              <div className={classes.okLine}>
                <CheckCircleIcon style={{ fontSize: 16 }} />
                {stats.docs.pct === 100
                  ? 'Todos los componentes con TechDocs'
                  : `${stats.docs.total - stats.docs.withDocs} pendientes de documentar`}
              </div>
            </Paper>
          </RouterLink>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <RouterLink to="/ai" className={classes.linkReset}>
            <Paper className={classes.card} elevation={0}>
              <div className={classes.cardTop}>
                <span className={classes.cardTitle}>
                  <span className={classes.iconWrap}>
                    <ChatIcon fontSize="small" />
                  </span>
                  Chunks AI
                </span>
                <Pill tone="accent">RAG index</Pill>
              </div>
              <Typography className={classes.value}>
                {stats.ai.chunks.toLocaleString('es-CO')}
              </Typography>
              <Sparkline values={sparkValues} />
              <div className={classes.sourceChips}>
                {Object.entries(stats.ai.bySource)
                  .slice(0, 4)
                  .map(([source, n]) => (
                    <span key={source} className={classes.chip}>
                      {source}: {n.toLocaleString('es-CO')}
                    </span>
                  ))}
                {Object.keys(stats.ai.bySource).length === 0 && (
                  <span className={classes.chip}>Sin índice aún</span>
                )}
              </div>
            </Paper>
          </RouterLink>
        </Grid>

        {/* Secondary mini cards */}
        <Grid item xs={12} sm={6} md={3}>
          <RouterLink to="/catalog" className={classes.linkReset}>
            <Paper className={classes.miniCard} elevation={0}>
              <span className={classes.cardTitle}>
                <span className={classes.iconWrap}>
                  <AppsIcon fontSize="small" />
                </span>
                Sistemas
              </span>
              <Typography className={classes.valueSm} style={{ marginTop: 8 }}>
                {stats.systems}
              </Typography>
              <Typography className={classes.caption}>
                Agrupaciones de producto
              </Typography>
            </Paper>
          </RouterLink>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <RouterLink to="/applications" className={classes.linkReset}>
            <Paper className={classes.miniCard} elevation={0}>
              <span className={classes.cardTitle}>
                <span className={classes.iconWrap}>
                  <AppsIcon fontSize="small" />
                </span>
                Aplicaciones
              </span>
              <Typography className={classes.valueSm} style={{ marginTop: 8 }}>
                {stats.apps.count}
              </Typography>
              <Typography className={classes.caption}>
                {stats.apps.names.length
                  ? stats.apps.names.join(' · ')
                  : 'Productos agrupados'}
              </Typography>
            </Paper>
          </RouterLink>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <RouterLink to="/videos" className={classes.linkReset}>
            <Paper className={classes.miniCard} elevation={0}>
              <span className={classes.cardTitle}>
                <span className={classes.iconWrap}>
                  <VideoLibraryIcon fontSize="small" />
                </span>
                Videos
              </span>
              <Typography className={classes.valueSm} style={{ marginTop: 8 }}>
                {stats.videos}
              </Typography>
              <Typography className={classes.caption}>
                Tutoriales y onboarding
              </Typography>
            </Paper>
          </RouterLink>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <RouterLink to="/qeta" className={classes.linkReset}>
            <Paper className={classes.miniCard} elevation={0}>
              <span className={classes.cardTitle}>
                <span className={classes.iconWrap}>
                  <LiveHelpIcon fontSize="small" />
                </span>
                Preguntas Q&A
              </span>
              <Typography className={classes.valueSm} style={{ marginTop: 8 }}>
                {stats.qeta}
              </Typography>
              <Typography className={classes.caption}>
                {stats.qeta === 0
                  ? 'Sin preguntas abiertas'
                  : 'Conocimiento de la comunidad'}
              </Typography>
            </Paper>
          </RouterLink>
        </Grid>

        {/* Bottom panels */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.panel} elevation={0}>
            <div className={classes.panelTitle}>
              <WarningIcon
                style={{
                  color:
                    stats.policies.actionable.length > 0
                      ? '#fbbf24'
                      : '#22c55e',
                  fontSize: 20,
                }}
              />
              Políticas de stack
              {stats.policies.actionable.length > 0 && (
                <Pill tone="warn">Acción req.</Pill>
              )}
            </div>
            <Typography className={classes.caption}>
              {stats.policies.actionable.length > 0
                ? `${stats.policies.actionable.length} de ${stats.policies.total} requieren atención`
                : `${stats.policies.total} políticas al día`}
            </Typography>
            {stats.policies.actionable.slice(0, 3).map((p, i) => (
              <div key={p.id || i} className={classes.alertItem}>
                <div className={classes.alertName}>
                  <span>
                    {p.technology || p.name || 'Política'}
                    {p.version ? ` ${p.version}` : ''}
                  </span>
                  <span className={classes.severity}>
                    {p.status === 'banned' ? 'Crítica' : 'Alta'}
                  </span>
                </div>
                <Typography className={classes.caption} style={{ marginTop: 4 }}>
                  Estado: {p.status?.replace(/_/g, ' ')}
                </Typography>
              </div>
            ))}
            {stats.policies.actionable.length === 0 && (
              <div className={classes.okLine} style={{ marginTop: 16 }}>
                <CheckCircleIcon style={{ fontSize: 16 }} />
                Stack alineado a las políticas oficiales
              </div>
            )}
            <Box mt={2}>
              <Button
                component={RouterLink as any}
                to="/stack-policies"
                size="small"
                color="primary"
                startIcon={<TimelineIcon />}
                style={{ textTransform: 'none' }}
              >
                Ver políticas
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper className={classes.panel} elevation={0}>
            <div className={classes.panelTitle}>
              Salud y cobertura del ecosistema
            </div>
            <Typography className={classes.caption} style={{ marginBottom: 16 }}>
              Cumplimiento estimado a partir de TechDocs y políticas de stack
            </Typography>

            <div className={classes.healthRow}>
              <Typography className={classes.caption}>
                Cumplimiento del estándar
              </Typography>
              <Typography style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                {compliance}%
              </Typography>
            </div>
            <LinearProgress
              className={classes.healthBar}
              variant="determinate"
              value={compliance}
              color="primary"
            />

            <div className={classes.healthRow}>
              <Typography className={classes.caption}>
                Cobertura TechDocs
              </Typography>
              <Typography style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                {stats.docs.pct}%
              </Typography>
            </div>
            <LinearProgress
              className={classes.healthBar}
              variant="determinate"
              value={stats.docs.pct}
              style={{ backgroundColor: 'rgba(34,197,94,0.15)' }}
            />

            <Typography
              style={{ fontWeight: 700, fontSize: '0.85rem', marginTop: 8 }}
            >
              Actividad del portal
            </Typography>
            <div className={classes.activity}>
              <div className={classes.activityItem}>
                <span className={classes.activityDot} />
                {stats.ai.chunks.toLocaleString('es-CO')} chunks indexados para
                el asistente AI
              </div>
              <div className={classes.activityItem}>
                <span className={classes.activityDot} />
                {stats.components.total} componentes · {stats.apis.total} APIs ·{' '}
                {stats.systems} sistemas en catálogo
              </div>
              <div className={classes.activityItem}>
                <span className={classes.activityDot} />
                {stats.apps.count} aplicaciones · {stats.videos} videos ·{' '}
                {stats.qeta} preguntas Q&A
              </div>
            </div>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
